import { DatePipe, NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, type OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSelectSearchComponent } from 'ngx-mat-select-search';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiServiceService } from '../api-service.service';
import { CommonInfoTooltipComponent } from '../common-component-info-tooltip/common-component-info-tooltip.component';
import { CommonPaginationComponent } from '../common-component-pagination/common-component-pagination.component';
import { FotaInfoComponent } from '../fota-info/fota-info.component';
import { exportAsCsvFile } from '../shared/function/exportDataToCsv';
import { SharedValidators } from '../shared/validators/shared-validators';

@Component({
  selector: 'app-manual-fota',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatIcon,
    MatSelect,
    MatOption,
    NgxPaginationModule,
    DatePipe,
    CommonPaginationComponent,
    NgClass,
    MatInput,
    MatTooltip,
    MatFormField,
    MatLabel,
    MatSelectSearchComponent,
    MatError,
    CommonInfoTooltipComponent,
    FotaInfoComponent,
  ],
  templateUrl: './manual-fota.component.html',
  styleUrl: './manual-fota.component.scss',
})
export class DeviceManualFotaComponent implements OnInit, OnDestroy {
  private _onDestroy = new Subject<void>();

  projectName = environment.projectName;

  page = 1;
  size = 10;
  totalLength = 0;
  search = '';
  listOfBatch = [];

  deviceFOTAForm: FormGroup = this.formBuilder.group({});

  userPermission = [];

  deleteManualFota = false;
  deleteFotaBatch = false;
  viewFota = false;
  updateFota = false;
  createManualFota = false;

  imei = '';
  deviceDetails: {
    state: { state: string; _id: string };
    uid: string;
    firmwareVersion: string;
    ufw: string;
  } = {
    state: { state: '', _id: '' },
    uid: '',
    firmwareVersion: '',
    ufw: '',
  };
  stateDetails = [];
  stateList = [];
  filteredStateName = [];
  firmwareList = [];
  filteredFirmwareList = [];
  stateName = [];
  isSearched = false;
  manualFotaToggle = false;

  numberOfFOTAInitiatedCount = 0;

  stateNameFilterCtrl: FormControl = new FormControl('');
  ufwNameFilterCtrl: FormControl = new FormControl('');

  constructor(
    public apiMicroservice: ApiServiceService,
    private formBuilder: FormBuilder,
    public router: Router,
    private route: ActivatedRoute,
    private spinner: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.checkPermission();
    this.setDeviceFotaForm();
    if (sessionStorage.getItem('fotaDeviceHistoryImei')) {
      this.imei = sessionStorage.getItem('fotaDeviceHistoryImei') ?? '';
      this.deviceFOTAForm.get('imei')?.setValue(this.imei);
      this.search = this.deviceFOTAForm.get('imei')?.value;
      this.getFotaBatchDeviceDetails();
    }
  }
  // Cleanup subscriptions
  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  checkPermission() {
    this.userPermission = JSON.parse(
      this.apiMicroservice.decryptData(
        sessionStorage.getItem('userPermission') || '[]'
      )
    );
    this.userPermission.forEach(
      (element: {
        pageName: string;
        view: boolean;
        create: boolean;
        delete: boolean;
        update: boolean;
      }) => {
        if (element.pageName === 'Manual FOTA' && element.view === false) {
          this.navigateBack();
        }
        if (element.pageName === 'Manual FOTA' && element.create === true) {
          this.createManualFota = true;
        }
        if (element.pageName === 'Manual FOTA' && element.delete === true) {
          this.deleteManualFota = true;
        }
        if (element.pageName === 'FOTA Batch' && element.delete === true) {
          this.deleteFotaBatch = true;
        }
      }
    );
  }

  setDeviceFotaForm() {
    this.deviceFOTAForm = this.formBuilder.group({
      imei: new FormControl('', [
        SharedValidators.requiredAndNoEmptySpaces,
        SharedValidators.exactLengthValidator(15),
        SharedValidators.numericValidator,
      ]),
      uin: new FormControl('', [
        SharedValidators.requiredAndNoEmptySpaces,
        SharedValidators.exactLengthValidator(19),
      ]),
      assignedState: new FormControl(''),
      currentFirmwareVersion: new FormControl(''),
      lastFOTAInitiatedFor: new FormControl(''),

      stateItem: new FormControl(null, [Validators.required]), // ✅ required
      state: new FormControl(''),

      UFWItem: new FormControl({ value: null, disabled: true }, [
        Validators.required,
      ]), // ✅ required
      UFW: new FormControl({ value: '', disabled: true }),

      model: new FormControl(''),
      ais140: new FormControl(false),
      isFotaMandatory: new FormControl(false),
    });
  }

  getFotaBatchDeviceDetails() {
    this.spinner.show();
    this.apiMicroservice.getFOTADevicesHistory(this.search).subscribe({
      next: (res) => {
        if (res.status) {
          this.spinner.hide();
          this.isSearched = true;
          this.listOfBatch = res.data;
          this.imei = res.deviceResult[0]?.imei || this.imei;
          this.deviceDetails = res.deviceResult[0];
          this.deviceFOTAForm
            .get('assignedState')
            ?.setValue(this.deviceDetails?.state?.state || 'Unassign');
          this.deviceFOTAForm.get('imei')?.setValue(this.imei);
          this.deviceFOTAForm.get('uin')?.setValue(this.deviceDetails?.uid);
          this.deviceFOTAForm
            .get('currentFirmwareVersion')
            ?.setValue(this.deviceDetails?.firmwareVersion || '--');
          this.deviceFOTAForm
            .get('lastFOTAInitiatedFor')
            ?.setValue(this.deviceDetails?.ufw || '--');
          if (this.projectName === 'TCU4G') {
            this.deviceFOTAForm.get('model')?.setValue('4G');
          } else {
            this.deviceFOTAForm.get('model')?.setValue('2G');
          }
          this.totalLength = res.data.totalItems;

          this.apiMicroservice.openSnackbar('Fetched Successfully');
        } else {
          this.spinner.hide();
          this.listOfBatch = [];
          this.apiMicroservice.openSnackbar('Something went wrong');
          this.isSearched = false;
        }
      },
      error: (error: HttpErrorResponse) => {
        this.spinner.hide();
        const errorMessage =
          error.error?.message || 'Error fetching FOTA history';
        this.apiMicroservice.openSnackbar(errorMessage);
        this.listOfBatch = [];
        this.isSearched = false;
      },
    });
  }

  onPageChange(event: number) {
    this.page = event;
  }

  onSizeChange(event: number) {
    this.page = 1;
    this.size = event;
  }

  onSearch(event: string) {
    if (event.length !== 15 || isNaN(parseInt(event))) {
      this.apiMicroservice.openSnackbar('Please enter valid IMEI');
      this.deviceFOTAForm.get('imei')?.setValue(this.search);
    } else {
      this.page = 1;
      this.size = 10;
      this.search = event;
      this.imei = event;
      this.deviceFOTAForm.get('imei')?.setValue(this.search);
      sessionStorage.setItem('fotaDeviceHistoryImei', event);
      this.setDeviceFotaForm();
      this.deviceFOTAForm.reset();
      this.getFotaBatchDeviceDetails();
      this.manualFotaToggle = false;
    }
  }

  setManualFota() {
    this.manualFotaToggle = true;
    this.getAllServerFirmwareDetails();
  }

  getAllServerFirmwareDetails() {
    this.spinner.show();

    this.apiMicroservice.getAllServerFirmwareDetails().subscribe({
      next: (res) => {
        this.spinner.hide();

        if (!res.status) {
          this.apiMicroservice.openSnackbar('Something went wrong');
          return;
        }

        this.stateDetails = res.data;
        let finalStateList = [];
        if (this.deviceDetails?.state) {
          const deviceStateId = String(this.deviceDetails.state?._id);

          const allowedStates = this.stateDetails.filter(
            (item: {
              _id?: string;
              govtIp1?: string;
              govtIp2?: string;
              port1?: number;
              port2?: number;
              stateEnable?: boolean;
            }) => {
              const isSameState = String(item._id) === deviceStateId;

              const isDefaultState =
                !item.govtIp1 &&
                !item.govtIp2 &&
                (item.port1 === null || item.port1 === undefined) &&
                (item.port2 === null || item.port2 === undefined) &&
                !item.stateEnable;

              return isSameState || isDefaultState;
            }
          );

          finalStateList = [...allowedStates];
        } else {
          finalStateList = [...this.stateDetails];
        }
        this.filteredStateName = [...finalStateList];

        if (this.filteredStateName.length === 1) {
          this.deviceFOTAForm.patchValue({
            stateItem: this.filteredStateName[0],
          });
        }

        // ✅ STATE FILTER
        this.stateNameFilterCtrl.valueChanges
          .pipe(takeUntil(this._onDestroy))
          .subscribe((search: string) => {
            if (!search) {
              this.filteredStateName = [...finalStateList];
              return;
            }

            const filterValue = search.toLowerCase().trim();

            this.filteredStateName = finalStateList.filter(
              (item: { state?: string; stateAbbreviation?: string }) =>
                item.state?.toLowerCase().includes(filterValue) ||
                item.stateAbbreviation?.toLowerCase().includes(filterValue)
            );
          });

        // ✅ HANDLE STATE CHANGE (ONLY ONCE)
        this.handleStateChange();

        // ✅ HANDLE FIRMWARE FILTER (ONLY ONCE)
        this.handleFirmwareFilter();

        // ✅ HANDLE FIRMWARE SELECTION
        this.handleFirmwareSelection();
      },

      error: (err) => {
        this.spinner.hide();
        this.apiMicroservice.openSnackbar(err?.error?.message || 'Error');
      },
    });
  }

  handleStateChange() {
    this.deviceFOTAForm
      .get('stateItem')
      ?.valueChanges.pipe(takeUntil(this._onDestroy))
      .subscribe((selectedState) => {
        if (!selectedState) {
          this.filteredFirmwareList = [];
          this.deviceFOTAForm.get('UFWItem')?.reset();
          this.deviceFOTAForm.get('UFWItem')?.disable();
          return;
        }

        // set state string
        this.deviceFOTAForm.get('state')?.setValue(selectedState.state);

        // extract firmware
        this.firmwareList = selectedState.firmware || [];
        this.filteredFirmwareList = [...this.firmwareList];

        // enable firmware dropdown
        this.deviceFOTAForm.get('UFWItem')?.reset();
        this.deviceFOTAForm.get('UFWItem')?.enable();
      });
  }

  handleFirmwareFilter() {
    this.ufwNameFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe((search: string) => {
        if (!search) {
          this.filteredFirmwareList = [...this.firmwareList];
          return;
        }

        const filterValue = search.toLowerCase().trim();

        this.filteredFirmwareList = this.firmwareList.filter(
          (fw: { firmwareVersion?: string; fileName?: string }) =>
            fw.firmwareVersion?.toLowerCase().includes(filterValue) ||
            fw.fileName?.toLowerCase().includes(filterValue)
        );
      });
  }

  handleFirmwareSelection() {
    this.deviceFOTAForm
      .get('UFWItem')
      ?.valueChanges.pipe(takeUntil(this._onDestroy))
      .subscribe((selectedFirmware) => {
        if (!selectedFirmware) {
          this.deviceFOTAForm.get('UFW')?.reset();
          return;
        }

        this.deviceFOTAForm
          .get('UFW')
          ?.setValue(selectedFirmware.firmwareVersion);
      });
  }

  isFirmwareDetailsFormValid(): boolean {
    return this.deviceFOTAForm.valid;
  }

  abort(id: string) {
    if (confirm('Are you sure to Abort the device FOTA?')) {
      this.spinner.show();
      const abortedBy = this.apiMicroservice.decryptData(
        sessionStorage.getItem('userRefId') || ''
      );
      this.apiMicroservice.abortFotaDevice(id, abortedBy).subscribe({
        next: (res) => {
          if (res.status) {
            this.apiMicroservice.openSnackbar('Aborted Successfully');
            this.ngOnInit();
          } else {
            this.apiMicroservice.openSnackbar('Something went wrong');
          }
        },
        error: (ERROR: HttpErrorResponse) => {
          this.spinner.hide();
          const example = ERROR.error.message;
          this.apiMicroservice.openSnackbar(example);
          this.ngOnInit();
        },
      });
    }
  }

  submitManualFota() {
    if (this.isFirmwareDetailsFormValid()) {
      this.spinner.show();
      const createdBy = this.apiMicroservice.decryptData(
        sessionStorage.getItem('userRefId') || ''
      );

      const payload = {
        IMEI: this.deviceFOTAForm.get('imei')?.value,
        UIN: this.deviceFOTAForm.get('uin')?.value,
        MODEL: this.deviceFOTAForm.get('model')?.value,
        STATE: this.deviceFOTAForm.get('state')?.value,
        UFW: this.deviceFOTAForm.get('UFW')?.value,
        ais140: this.deviceFOTAForm.get('ais140')?.value,
        isFotaMandatory: this.deviceFOTAForm.get('isFotaMandatory')?.value,
        createdBy: createdBy,
      };

      this.apiMicroservice.createManualFota(payload).subscribe({
        next: (res) => {
          this.spinner.hide();
          if (res.status) {
            this.apiMicroservice.openSnackbar(
              'Manual FOTA Created Successfully'
            );
            this.deviceFOTAForm.reset();
            this.manualFotaToggle = false;
            this.getFotaBatchDeviceDetails();
          } else {
            this.apiMicroservice.openSnackbar(
              res.message || 'Failed to create Manual FOTA'
            );
          }
        },
        error: (ERROR: HttpErrorResponse) => {
          this.spinner.hide();
          const errorMessage =
            ERROR.error?.message || 'Error creating Manual FOTA';
          this.apiMicroservice.openSnackbar(errorMessage);
        },
      });
    }
  }

  exportToExcel() {
    if (confirm('Do you want to download this history details?')) {
      this.spinner.show();
      if (this.listOfBatch.length > 0) {
        const exportData = this.listOfBatch.map(
          (item: {
            batchId?: { batchId: string };
            createdAt?: Date;
            ais140?: boolean;
            isFotaMandatory: boolean;
            uin?: string;
            imei?: string;
            model?: string;
            state?: string;
            currentFirmwareVersion?: string;
            targetFirmwareVersion?: string;
            lastDevicePing?: Date;
            pingCount?: number;
            addedToBatch?: boolean;
            progress?: number;
            deviceFotaStatus?: string;
            attemptCount?: number;
            startedAt?: Date;
            completedAt?: Date;
            attempts?: {
              attemptNumber: number;
              startedAt: string | Date;
              completedAt: string | Date | null;
              outcome: string | null;
            }[];
            stateEnableSetStatus?: boolean;
            stateEnableProcessedAt?: Date;
            govtIp1SetStatus?: boolean;
            govtIp1ProcessedAt?: Date;
            govtIp1?: string;
            govtPort1?: string;
            govtIp2SetStatus?: boolean;
            govtIp2ProcessedAt?: Date;
            govtIp2?: string;
            govtPort2?: string;
            secondaryStateEnableSetStatus?: boolean;
            secondaryStateEnableProcessedAt?: Date;
            govtIp3SetStatus?: boolean;
            govtIp3ProcessedAt?: Date;
            govtIp3?: string;
            govtPort3?: string;
            govtIp4SetStatus?: boolean;
            govtIp4ProcessedAt?: Date;
            govtIp4?: string;
            govtPort4?: string;
            isAborted?: boolean;
            abortReason?: string;
            deviceFotaCompletionStatus?: boolean;
            updatedAt?: Date;
          }) => ({
            'Batch Id': item.batchId?.batchId,
            'Created At': item.createdAt
              ? new Date(item.createdAt).toLocaleString('en-GB', {
                  timeZone: 'Asia/Kolkata',
                  hour12: true,
                })
              : '',
            'FOTA Type': item.ais140 ? 'AIS 140' : 'Regular',
            'Is Mandatory': item.isFotaMandatory ? 'Yes' : 'No',
            UIN: item.uin,
            IMEI: item.imei,
            Model: item.model,
            State: item.state,
            AF: item.currentFirmwareVersion,
            UFW: item.targetFirmwareVersion,
            'Last Login': item.lastDevicePing
              ? new Date(item.lastDevicePing).toLocaleString('en-GB', {
                  timeZone: 'Asia/Kolkata',
                  hour12: true,
                })
              : '',
            'FOTA Resume Count': item.pingCount,
            'Added To Batch': item.addedToBatch ? 'Yes' : 'No',
            'FOTA Download Percentage': item.progress,
            'FOTA Status': item.deviceFotaStatus,
            'FOTA Attempt Count': item.attemptCount,
            'FOTA Download Started At': item.startedAt
              ? new Date(item.startedAt).toLocaleString('en-GB', {
                  timeZone: 'Asia/Kolkata',
                  hour12: true,
                })
              : '',
            'FOTA Download Completed At': item.completedAt
              ? new Date(item.completedAt).toLocaleString('en-GB', {
                  timeZone: 'Asia/Kolkata',
                  hour12: true,
                })
              : '',
            'FOTA Attempts Log': this.formatAttemptsLog(item.attempts),
            '1st SEO Status': item.stateEnableSetStatus,
            '1st SEO Processed At': item.stateEnableProcessedAt
              ? new Date(item.stateEnableProcessedAt).toLocaleString('en-GB', {
                  timeZone: 'Asia/Kolkata',
                  hour12: true,
                })
              : '',
            'Primary IP Status': item.govtIp1SetStatus,
            'Primary IP Processed At': item.govtIp1ProcessedAt
              ? new Date(item.govtIp1ProcessedAt).toLocaleString('en-GB', {
                  timeZone: 'Asia/Kolkata',
                  hour12: true,
                })
              : '',
            'Secondary IP Status': item.govtIp2SetStatus,
            'Secondary IP Processed At': item.govtIp2ProcessedAt
              ? new Date(item.govtIp2ProcessedAt).toLocaleString('en-GB', {
                  timeZone: 'Asia/Kolkata',
                  hour12: true,
                })
              : '',
            '2nd SEO Status':
              item.secondaryStateEnableSetStatus || 'NA',
            '2nd SEO Processed At':
              item.secondaryStateEnableProcessedAt
                ? new Date(item.secondaryStateEnableProcessedAt).toLocaleString(
                    'en-GB',
                    {
                      timeZone: 'Asia/Kolkata',
                      hour12: true,
                    }
                  )
                : '',
            'Third IP Status': item.govtIp3SetStatus,
            'Third IP Processed At': item.govtIp3ProcessedAt
              ? new Date(item.govtIp3ProcessedAt).toLocaleString('en-GB', {
                  timeZone: 'Asia/Kolkata',
                  hour12: true,
                })
              : '',
            'Forth IP Status': item.govtIp4SetStatus,
            'Forth IP Processed At': item.govtIp4ProcessedAt
              ? new Date(item.govtIp4ProcessedAt).toLocaleString('en-GB', {
                  timeZone: 'Asia/Kolkata',
                  hour12: true,
                })
              : '',
            'Is Aborted': item.isAborted,
            'Abort Reason': item.abortReason,
            'Is FOTA Completed': item.deviceFotaCompletionStatus,
            'Device FOTA Updated At': item.updatedAt
              ? new Date(item.updatedAt).toLocaleString('en-GB', {
                  timeZone: 'Asia/Kolkata',
                  hour12: true,
                })
              : '--',
          })
        );

        const fileName = `Device_FOTA_History_${this.imei}`;
        exportAsCsvFile(exportData, fileName);
        this.spinner.hide();
      } else {
        this.apiMicroservice.openSnackbar('No Data Found');
        this.spinner.hide();
      }
    }
  }

  getGovtIp1Tooltip(device: {
    govtIp1SetStatus: boolean;
    govtIp1ProcessedAt: Date;
    govtIp1: string;
    govtPort1: string;
  }): string {
    if (!device.govtIp1SetStatus) {
      return 'No Govt IP configured';
    }

    const date = device.govtIp1ProcessedAt
      ? new Date(device.govtIp1ProcessedAt).toLocaleString()
      : '--';

    return `Govt IP : ${device.govtIp1 || '--'}
Port    : ${device.govtPort1 || '--'}
Processed At: ${date}`;
  }

  getGovtIp2Tooltip(device: {
    govtIp2SetStatus: boolean;
    govtIp2ProcessedAt: Date;
    govtIp2: string;
    govtPort2: string;
  }): string {
    if (!device.govtIp2SetStatus) {
      return 'No Govt IP configured';
    }

    const date = device.govtIp2ProcessedAt
      ? new Date(device.govtIp2ProcessedAt).toLocaleString()
      : '--';

    return `Govt IP : ${device.govtIp2 || '--'}
Port    : ${device.govtPort2 || '--'}
Processed At: ${date}`;
  }

  getGovtIp3Tooltip(device: {
    govtIp3SetStatus: string;
    govtIp3ProcessedAt: string;
    govtIp3: string;
    govtPort3: string;
  }): string {
    if (!device.govtIp3SetStatus) {
      return 'No Govt IP configured';
    }

    const date = device.govtIp3ProcessedAt
      ? new Date(device.govtIp3ProcessedAt).toLocaleString()
      : '--';

    return `Govt IP : ${device.govtIp3 || '--'}
Port    : ${device.govtPort3 || '--'}
Processed At: ${date}`;
  }

  getGovtIp4Tooltip(device: {
    govtIp4SetStatus: string;
    govtIp4ProcessedAt: string;
    govtIp4: string;
    govtPort4: string;
  }): string {
    if (!device.govtIp4SetStatus) {
      return 'No Govt IP configured';
    }

    const date = device.govtIp4ProcessedAt
      ? new Date(device.govtIp4ProcessedAt).toLocaleString()
      : '--';

    return `Govt IP : ${device.govtIp4 || '--'}
Port    : ${device.govtPort4 || '--'}
Processed At: ${date}`;
  }

  get1stSEOTooltip(device: {
    stateEnableSetStatus: boolean;
    stateEnableProcessedAt: Date;
    stateEnable: string;
  }): string {
    if (!device.stateEnableSetStatus) {
      return 'No Govt IP configured';
    }

    const date = device.stateEnableProcessedAt
      ? new Date(device.stateEnableProcessedAt).toLocaleString()
      : '--';

    return `1st SEO Command : ${device.stateEnable || '--'}
Processed At: ${date}`;
  }

  get2ndSEOTooltip(device: {
    secondaryStateEnableSetStatus: string;
    secondaryStateEnableProcessedAt: string;
    secondaryStateEnable: string;
  }): string {
    if (!device.secondaryStateEnableSetStatus) {
      return 'No Govt IP configured';
    }

    const date = device.secondaryStateEnableProcessedAt
      ? new Date(device.secondaryStateEnableProcessedAt).toLocaleString()
      : '--';

    return `2nd SEO Command : ${device.secondaryStateEnable || '--'}
Processed At: ${date}`;
  }

  getPingCountTooltip(device: { pingCount: number }): string {
    return `Number of times the device resumed FOTA download: ${device.pingCount || '--'}`;
  }

  downloadDurationTooltip(device: {
    startedAt: Date;
    completedAt: Date;
  }): string {
    return `${device.startedAt ? new Date(device.startedAt).toLocaleString() : '--'} 
    to  ${device.completedAt ? new Date(device.completedAt).toLocaleString() : '--'}`;
  }

  getRetryCountTooltip(device: {
    attemptCount: string;
    attempts?: {
      attemptNumber: number;
      startedAt: string | Date;
      completedAt: string | Date | null;
      outcome: string | null;
    }[];
  }): string {
    const header = `FOTA Download Attempts: ${device.attemptCount || '--'}`;
    if (!device.attempts || device.attempts.length === 0) {
      return header;
    }
    const lines = device.attempts.map((attempt) => {
      const start = attempt.startedAt
        ? new Date(attempt.startedAt).toLocaleString()
        : '--';
      const end = attempt.completedAt
        ? new Date(attempt.completedAt).toLocaleString()
        : 'in progress';
      const outcome = attempt.outcome || 'in progress';
      return `#${attempt.attemptNumber}: ${start} → ${end}  (${outcome})`;
    });
    return `${header}\n${lines.join('\n')}`;
  }

  formatAttemptsLog(
    attempts?: {
      attemptNumber: number;
      startedAt: string | Date;
      completedAt: string | Date | null;
      outcome: string | null;
    }[]
  ): string {
    if (!attempts || attempts.length === 0) return '';
    return attempts
      .map((attempt) => {
        const start = attempt.startedAt
          ? new Date(attempt.startedAt).toLocaleString('en-GB', {
              timeZone: 'Asia/Kolkata',
              hour12: true,
            })
          : '--';
        const end = attempt.completedAt
          ? new Date(attempt.completedAt).toLocaleString('en-GB', {
              timeZone: 'Asia/Kolkata',
              hour12: true,
            })
          : 'In Progress';
        const outcome = attempt.outcome || 'In Progress';
        return `#${attempt.attemptNumber}: ${start} to ${end} (${outcome})`;
      })
      .join(' | ');
  }

  redirect(to: string): void {
    this.router.navigate([to]);
  }

  navigateBack() {
    this.apiMicroservice.back();
  }
}
